import { useState, useCallback, useRef, useEffect } from 'react'
import { Plus, X, Check, GripHorizontal, RefreshCw, Heading, LayoutGrid, BarChart2 } from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ResponsiveGridLayout, useContainerWidth, type Layout, type LayoutItem } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import api from '@/lib/api'
import { PageHeader, Button, Modal, LoadingState, ErrorState, EmptyState } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useStatsOverview, STATS_QUERY_KEY } from '@/hooks/stats'
import { STAT_CARD_REGISTRY, STAT_CARD_CATEGORIES, getCardDef } from '@/components/stats/statCardRegistry'
import { ChartTile } from '@/components/stats/ChartTile'
import { CHART_CARD_REGISTRY, CHART_CARD_CATEGORIES, getChartCardDef } from '@/components/stats/chartCardRegistry'
import { useTenantStore } from '@/stores/tenants'

// ─── Types ────────────────────────────────────────────────────────────────────

export type TileVariant = 'compact' | 'large' | 'accent' | 'minimal'
export type TileType = 'stat-card' | 'heading' | 'chart'

interface DashboardTile {
  i: string
  type?: TileType
  cardKey?: string
  chartKey?: string
  text?: string
  variant?: TileVariant
  x: number
  y: number
  w: number
  h: number
}

interface DashboardTab {
  id: string
  label: string
  tiles: DashboardTile[]
}

interface DashboardConfig {
  tabs: DashboardTab[]
}

// ─── Defaults ─────────────────────────────────────────────────────────────────

const DEFAULT_CONFIG: DashboardConfig = {
  tabs: [
    {
      id: 'overview',
      label: 'Übersicht',
      tiles: [
        { i: 'tickets.allOpen',             type: 'stat-card', cardKey: 'tickets.allOpen',             x: 0, y: 0, w: 3, h: 2 },
        { i: 'tickets.overdue',             type: 'stat-card', cardKey: 'tickets.overdue',             x: 3, y: 0, w: 3, h: 2 },
        { i: 'tickets.unassigned',          type: 'stat-card', cardKey: 'tickets.unassigned',          x: 6, y: 0, w: 3, h: 2 },
        { i: 'tickets.own',                 type: 'stat-card', cardKey: 'tickets.own',                 x: 9, y: 0, w: 3, h: 2 },
        { i: 'calls.missed',                type: 'stat-card', cardKey: 'calls.missed',                x: 0, y: 2, w: 3, h: 2 },
        { i: 'invoices.overdue.count',      type: 'stat-card', cardKey: 'invoices.overdue.count',      x: 3, y: 2, w: 3, h: 2 },
        { i: 'invoices.overdue.totalGross', type: 'stat-card', cardKey: 'invoices.overdue.totalGross', x: 6, y: 2, w: 3, h: 2 },
        { i: 'opos.debtors.overdueCount',   type: 'stat-card', cardKey: 'opos.debtors.overdueCount',   x: 9, y: 2, w: 3, h: 2 },
      ],
    },
  ],
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

const CONFIG_QUERY_KEY = ['stats', 'tabs-config'] as const

function useDashboardConfig() {
  return useQuery<{ config: DashboardConfig | null }>({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: () => api.get('/stats/tabs-config').then((r: { data: { config: DashboardConfig | null } }) => r.data),
    staleTime: Infinity,
  })
}

function useSaveDashboardConfig() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (config: DashboardConfig) => api.put('/stats/tabs-config', { config }),
    onSuccess: (_: unknown, config: DashboardConfig) => {
      qc.setQueryData(CONFIG_QUERY_KEY, { config })
    },
  })
}

// ─── Stat value helpers ────────────────────────────────────────────────────────

import type { StatsOverview } from '@/hooks/stats'
import type { StatCardDef } from '@/components/stats/statCardRegistry'
import { AlertCircle, CheckCircle2, Minus } from 'lucide-react'
import { Skeleton, Card } from '@/components/ui'

function formatValue(value: number, format: StatCardDef['format']): string {
  if (format === 'currency') {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value)
  }
  if (format === 'days') return `${value} Tage`
  if (format === 'percent') return `${value} %`
  return value.toLocaleString('de-DE')
}

// ─── Stat Card Variants ───────────────────────────────────────────────────────

function StatTile({
  def,
  data,
  loading,
  variant = 'compact',
}: {
  def: StatCardDef
  data: StatsOverview | undefined
  loading: boolean
  variant?: TileVariant
}) {
  const value = data ? def.getValue(data) : undefined
  const hasValue = value != null
  const isWarn = def.warnIfPositive && hasValue && value > 0
  const isGood = def.goodIfPositive && hasValue && value > 0

  if (variant === 'minimal') {
    return (
      <div className="h-full flex flex-col justify-center px-2">
        <p className="text-xs text-gray-400 mb-0.5 truncate">{def.label}</p>
        {loading ? (
          <Skeleton className="h-6 w-16" />
        ) : !hasValue ? (
          <span className="text-lg text-gray-300">—</span>
        ) : (
          <p className={cn('text-lg font-semibold leading-none',
            isWarn && 'text-red-600', isGood && 'text-green-600', !isWarn && !isGood && 'text-gray-900')}>
            {formatValue(value, def.format)}
          </p>
        )}
      </div>
    )
  }

  if (variant === 'large') {
    return (
      <Card className="h-full">
        <Card.Body className="h-full flex flex-col justify-between p-4">
          <p className="text-sm text-gray-500 truncate">{def.label}</p>
          {loading ? (
            <Skeleton className="h-12 w-24 mt-2" />
          ) : !hasValue ? (
            <div className="flex items-center gap-1 text-gray-300"><Minus className="w-5 h-5" /><span className="text-4xl font-bold">—</span></div>
          ) : (
            <div className="flex items-end justify-between gap-2 mt-1">
              <p className={cn('text-4xl font-bold leading-none',
                isWarn && 'text-red-600', isGood && 'text-green-600', !isWarn && !isGood && 'text-gray-900')}>
                {formatValue(value, def.format)}
              </p>
              {isWarn && <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mb-1" />}
              {isGood && <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0 mb-1" />}
            </div>
          )}
        </Card.Body>
      </Card>
    )
  }

  if (variant === 'accent') {
    const bg = isWarn ? 'bg-red-50 border-red-200' : isGood ? 'bg-green-50 border-green-200' : 'bg-brand-50 border-brand-200'
    const textVal = isWarn ? 'text-red-700' : isGood ? 'text-green-700' : 'text-brand-700'
    const textLabel = isWarn ? 'text-red-500' : isGood ? 'text-green-500' : 'text-brand-500'
    return (
      <div className={cn('h-full rounded-lg border p-4 flex flex-col justify-between', bg)}>
        <p className={cn('text-xs font-medium truncate', textLabel)}>{def.label}</p>
        {loading ? (
          <Skeleton className="h-8 w-20 mt-1" />
        ) : !hasValue ? (
          <span className="text-2xl font-bold text-gray-300">—</span>
        ) : (
          <div className="flex items-end justify-between gap-2 mt-1">
            <p className={cn('text-2xl font-bold leading-none', textVal)}>
              {formatValue(value, def.format)}
            </p>
            {isWarn && <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mb-0.5" />}
            {isGood && <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0 mb-0.5" />}
          </div>
        )}
      </div>
    )
  }

  // compact (default)
  return (
    <Card className="h-full">
      <Card.Body className="h-full p-4">
        <p className="text-small text-gray-500 mb-2 truncate">{def.label}</p>
        {loading ? (
          <Skeleton className="h-8 w-20 mt-1" />
        ) : !hasValue ? (
          <div className="flex items-center gap-1 text-gray-300">
            <Minus className="w-4 h-4" />
            <span className="text-h2">—</span>
          </div>
        ) : (
          <div className="flex items-end justify-between gap-2">
            <p className={cn('text-h1 leading-none',
              isWarn && 'text-red-600', isGood && 'text-green-600', !isWarn && !isGood && 'text-gray-900')}>
              {formatValue(value, def.format)}
            </p>
            {isWarn && <AlertCircle className="w-4 h-4 text-red-400 shrink-0 mb-0.5" />}
            {isGood && <CheckCircle2 className="w-4 h-4 text-green-400 shrink-0 mb-0.5" />}
          </div>
        )}
      </Card.Body>
    </Card>
  )
}

// ─── Inline text editor (for headings + tab labels) ───────────────────────────

function InlineEditor({
  value,
  onSave,
  className,
}: {
  value: string
  onSave: (v: string) => void
  className?: string
}) {
  const [draft, setDraft] = useState(value)
  const ref = useRef<HTMLInputElement>(null)
  useEffect(() => { ref.current?.select() }, [])

  function commit() {
    const t = draft.trim()
    onSave(t || value)
  }

  return (
    <input
      ref={ref}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.preventDefault(); commit() }
        if (e.key === 'Escape') { setDraft(value); ref.current?.blur() }
        e.stopPropagation()
      }}
      onClick={(e) => e.stopPropagation()}
      className={cn('bg-transparent outline-none border-b border-brand-400', className)}
    />
  )
}

// ─── Variant picker bar ───────────────────────────────────────────────────────

const VARIANTS: { key: TileVariant; label: string }[] = [
  { key: 'compact', label: 'Kompakt' },
  { key: 'large', label: 'Groß' },
  { key: 'accent', label: 'Farbig' },
  { key: 'minimal', label: 'Minimal' },
]

function VariantPicker({
  current,
  onChange,
}: {
  current: TileVariant
  onChange: (v: TileVariant) => void
}) {
  return (
    <div className="absolute bottom-1 left-1 right-6 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity z-20 pointer-events-none group-hover:pointer-events-auto">
      {VARIANTS.map((v) => (
        <button
          key={v.key}
          type="button"
          onClick={(e) => { e.stopPropagation(); onChange(v.key) }}
          className={cn(
            'flex-1 text-[10px] py-0.5 rounded transition-colors',
            current === v.key
              ? 'bg-brand-500 text-white'
              : 'bg-white/90 text-gray-500 hover:bg-brand-50 hover:text-brand-600 border border-gray-200',
          )}
        >
          {v.label}
        </button>
      ))}
    </div>
  )
}

// ─── Tile Picker Modal ────────────────────────────────────────────────────────

function TilePickerModal({
  open,
  onOpenChange,
  existingKeys,
  onAddCard,
  onAddChart,
  onAddHeading,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  existingKeys: Set<string>
  onAddCard: (cardKey: string) => void
  onAddChart: (chartKey: string) => void
  onAddHeading: () => void
}) {
  return (
    <Modal open={open} onOpenChange={onOpenChange} title="Kachel hinzufügen" size="xl">
      <div className="space-y-5 max-h-[60vh] overflow-y-auto pr-1">
        {/* Section heading */}
        <div>
          <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
            Layout
          </h3>
          <button
            type="button"
            onClick={() => { onAddHeading(); onOpenChange(false) }}
            className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50 text-gray-700 text-sm w-full text-left transition-colors"
          >
            <Heading className="w-4 h-4 text-gray-400 shrink-0" />
            Zwischenüberschrift
          </button>
        </div>

        {/* Chart Cards */}
        {CHART_CARD_CATEGORIES.map((category) => {
          const charts = CHART_CARD_REGISTRY.filter((c) => c.category === category)
          return (
            <div key={category}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {category}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {charts.map((chart) => (
                  <button
                    key={chart.key}
                    type="button"
                    onClick={() => { onAddChart(chart.key); onOpenChange(false) }}
                    className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 hover:border-brand-300 hover:bg-brand-50 text-gray-700 text-left transition-colors text-sm"
                  >
                    <BarChart2 className="w-4 h-4 text-gray-400 shrink-0" />
                    {chart.label}
                  </button>
                ))}
              </div>
            </div>
          )
        })}

        {/* Stat Cards */}
        {STAT_CARD_CATEGORIES.map((category) => {
          const cards = STAT_CARD_REGISTRY.filter((c) => c.category === category)
          return (
            <div key={category}>
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                {category}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {cards.map((card) => {
                  const already = existingKeys.has(card.key)
                  return (
                    <button
                      key={card.key}
                      type="button"
                      disabled={already}
                      onClick={() => { onAddCard(card.key); onOpenChange(false) }}
                      className={cn(
                        'flex items-center gap-3 p-3 rounded-lg border text-left transition-colors text-sm',
                        already
                          ? 'border-gray-100 bg-gray-50 text-gray-300 cursor-not-allowed'
                          : 'border-gray-200 hover:border-brand-300 hover:bg-brand-50 text-gray-700',
                      )}
                    >
                      {card.label}
                      {already && <span className="ml-auto text-xs text-gray-300">vorhanden</span>}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </Modal>
  )
}

// ─── Tab Bar ──────────────────────────────────────────────────────────────────

function TabBar({
  tabs,
  activeId,
  isEditing,
  onSelect,
  onRename,
  onDelete,
  onAdd,
}: {
  tabs: DashboardTab[]
  activeId: string | null
  isEditing: boolean
  onSelect: (id: string) => void
  onRename: (id: string, label: string) => void
  onDelete: (id: string) => void
  onAdd: () => void
}) {
  const [editingId, setEditingId] = useState<string | null>(null)

  return (
    <div className="flex items-center gap-0 border-b border-gray-200 overflow-x-auto shrink-0">
      {tabs.map((tab) => (
        <div
          key={tab.id}
          className={cn(
            'flex items-center gap-1.5 px-4 py-2 border-b-2 cursor-pointer shrink-0 transition-colors select-none',
            tab.id === activeId
              ? 'border-brand-500 text-brand-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
          )}
          onClick={() => { if (editingId !== tab.id) onSelect(tab.id) }}
        >
          {isEditing && editingId === tab.id ? (
            <InlineEditor
              value={tab.label}
              onSave={(v) => { onRename(tab.id, v); setEditingId(null) }}
              className="text-sm font-medium w-28"
            />
          ) : (
            <span
              className="text-sm font-medium"
              onDoubleClick={isEditing ? (e) => { e.stopPropagation(); setEditingId(tab.id) } : undefined}
              title={isEditing ? 'Doppelklick zum Umbenennen' : undefined}
            >
              {tab.label}
            </span>
          )}
          {isEditing && tabs.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onDelete(tab.id) }}
              className="w-4 h-4 rounded-full text-gray-300 hover:text-red-400 flex items-center justify-center transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      ))}
      {isEditing && (
        <button
          type="button"
          onClick={onAdd}
          className="flex items-center gap-1 px-3 py-2 text-sm text-gray-400 hover:text-brand-500 transition-colors shrink-0"
        >
          <Plus className="w-4 h-4" />
          Tab
        </button>
      )}
    </div>
  )
}

// ─── Tab Grid ─────────────────────────────────────────────────────────────────

function TabGrid({
  tiles,
  isEditing,
  statsData,
  statsLoading,
  onLayoutChange,
  onRemove,
  onVariantChange,
  onHeadingChange,
}: {
  tiles: DashboardTile[]
  isEditing: boolean
  statsData: StatsOverview | undefined
  statsLoading: boolean
  onLayoutChange: (tiles: DashboardTile[]) => void
  onRemove: (i: string) => void
  onVariantChange: (i: string, variant: TileVariant) => void
  onHeadingChange: (i: string, text: string) => void
}) {
  const { width, containerRef } = useContainerWidth()

  const layoutItems: LayoutItem[] = tiles.map((t) => {
    const type = t.type ?? 'stat-card'
    const isHeading = type === 'heading'
    const isChart = type === 'chart'
    return {
      i: t.i,
      x: t.x,
      y: t.y,
      w: t.w,
      h: t.h,
      minW: isHeading ? 2 : 2,
      minH: isHeading ? 1 : isChart ? 3 : 2,
      static: !isEditing,
    }
  })

  function handleLayoutChange(newLayout: Layout) {
    if (!isEditing) return
    const updated = tiles.map((tile) => {
      const l = newLayout.find((n: LayoutItem) => n.i === tile.i)
      return l ? { ...tile, x: l.x, y: l.y, w: l.w, h: l.h } : tile
    })
    onLayoutChange(updated)
  }

  if (tiles.length === 0) {
    return (
      <EmptyState
        title="Noch keine Kacheln"
        description='Klick auf "Kachel hinzufügen" um Kennzahlen auf diesem Tab anzuzeigen.'
      />
    )
  }

  return (
    <div ref={containerRef as React.RefObject<HTMLDivElement>}>
      {width > 0 && (
        <ResponsiveGridLayout
          className="layout"
          width={width}
          layouts={{ lg: layoutItems }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 12, sm: 12, xs: 6, xxs: 4 }}
          rowHeight={44}
          dragConfig={{ enabled: isEditing, handle: '.drag-handle' }}
          resizeConfig={{ enabled: isEditing }}
          onLayoutChange={handleLayoutChange}
          margin={[10, 10] as const}
        >
          {tiles.map((tile) => {
            const tileType = tile.type ?? 'stat-card'

            if (tileType === 'heading') {
              return (
                <div key={tile.i} className="relative group flex items-end h-full">
                  {isEditing && (
                    <div className="drag-handle absolute top-0 left-0 right-0 h-5 flex items-center justify-center cursor-grab active:cursor-grabbing z-10">
                      <GripHorizontal className="w-4 h-4 text-gray-300 group-hover:text-gray-400" />
                    </div>
                  )}
                  <div className={cn('w-full border-b border-gray-300 pb-1', isEditing && 'pt-5')}>
                    <HeadingTileContent
                      text={tile.text ?? 'Überschrift'}
                      isEditing={isEditing}
                      onSave={(v) => onHeadingChange(tile.i, v)}
                    />
                  </div>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => onRemove(tile.i)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-500 flex items-center justify-center z-20 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )
            }

            if (tileType === 'chart') {
              const chartDef = tile.chartKey ? getChartCardDef(tile.chartKey) : undefined
              if (!chartDef) return <div key={tile.i} />
              return (
                <div key={tile.i} className="relative group h-full">
                  {isEditing && (
                    <div className="drag-handle absolute inset-x-0 top-0 h-5 flex items-center justify-center cursor-grab active:cursor-grabbing z-10">
                      <GripHorizontal className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                    </div>
                  )}
                  <div className={cn('h-full', isEditing && 'pt-4')}>
                    <ChartTile def={chartDef} />
                  </div>
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => onRemove(tile.i)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-500 flex items-center justify-center z-20 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              )
            }

            const def = tile.cardKey ? getCardDef(tile.cardKey) : undefined
            if (!def) return <div key={tile.i} />

            return (
              <div key={tile.i} className="relative group h-full">
                {isEditing && (
                  <div className="drag-handle absolute inset-x-0 top-0 h-5 flex items-center justify-center cursor-grab active:cursor-grabbing z-10">
                    <GripHorizontal className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors" />
                  </div>
                )}
                <div className={cn('h-full', isEditing && 'pt-4')}>
                  <StatTile
                    def={def}
                    data={statsData}
                    loading={statsLoading}
                    variant={tile.variant ?? 'compact'}
                  />
                </div>
                {isEditing && (
                  <>
                    <button
                      type="button"
                      onClick={() => onRemove(tile.i)}
                      className="absolute top-1 right-1 w-5 h-5 rounded-full bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-500 flex items-center justify-center z-20 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <VariantPicker
                      current={tile.variant ?? 'compact'}
                      onChange={(v) => onVariantChange(tile.i, v)}
                    />
                  </>
                )}
              </div>
            )
          })}
        </ResponsiveGridLayout>
      )}
    </div>
  )
}

function HeadingTileContent({
  text,
  isEditing,
  onSave,
}: {
  text: string
  isEditing: boolean
  onSave: (v: string) => void
}) {
  const [editing, setEditing] = useState(false)

  if (isEditing && editing) {
    return (
      <InlineEditor
        value={text}
        onSave={(v) => { onSave(v); setEditing(false) }}
        className="text-sm font-semibold text-gray-600 uppercase tracking-wide w-full"
      />
    )
  }

  return (
    <span
      className={cn(
        'text-sm font-semibold text-gray-500 uppercase tracking-wide',
        isEditing && 'cursor-text hover:text-gray-700',
      )}
      onDoubleClick={isEditing ? () => setEditing(true) : undefined}
      title={isEditing ? 'Doppelklick zum Bearbeiten' : undefined}
    >
      {text}
    </span>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function StatsDashboardPage() {
  const qc = useQueryClient()
  const activeTenantIds = useTenantStore((s) => s.activeTenantIds)
  const configQuery = useDashboardConfig()
  const saveConfig = useSaveDashboardConfig()
  const stats = useStatsOverview(activeTenantIds)

  const config: DashboardConfig = configQuery.data?.config ?? DEFAULT_CONFIG
  const [activeTabId, setActiveTabId] = useState<string | null>(null)
  const [isEditing, setIsEditing] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)

  const currentTabId = activeTabId ?? config.tabs[0]?.id ?? null
  const currentTab = config.tabs.find((t) => t.id === currentTabId) ?? config.tabs[0]

  function save(updated: DashboardConfig) {
    saveConfig.mutate(updated)
  }

  function updateTabs(tabs: DashboardTab[]) {
    save({ ...config, tabs })
  }

  // ── Tab actions ──────────────────────────────────────────────────────────

  function addTab() {
    const id = `tab-${Date.now()}`
    updateTabs([...config.tabs, { id, label: `Tab ${config.tabs.length + 1}`, tiles: [] }])
    setActiveTabId(id)
  }

  function deleteTab(tabId: string) {
    if (config.tabs.length <= 1) return
    const next = config.tabs.filter((t) => t.id !== tabId)
    if (currentTabId === tabId) setActiveTabId(next[0]?.id ?? null)
    updateTabs(next)
  }

  function renameTab(tabId: string, label: string) {
    updateTabs(config.tabs.map((t) => (t.id === tabId ? { ...t, label } : t)))
  }

  // ── Tile helpers ─────────────────────────────────────────────────────────

  function patchTab(tabId: string, tiles: DashboardTile[]) {
    updateTabs(config.tabs.map((t) => (t.id === tabId ? { ...t, tiles } : t)))
  }

  const existingCardKeys = new Set(
    (currentTab?.tiles ?? []).filter((t) => (t.type ?? 'stat-card') === 'stat-card').map((t) => t.cardKey ?? ''),
  )

  const addCard = useCallback(
    (cardKey: string) => {
      if (!currentTab) return
      const maxY = currentTab.tiles.reduce((m, t) => Math.max(m, t.y + t.h), 0)
      patchTab(currentTabId!, [
        ...currentTab.tiles,
        { i: `${cardKey}-${Date.now()}`, type: 'stat-card', cardKey, x: 0, y: maxY, w: 3, h: 2 },
      ])
    },
    [config, currentTab, currentTabId],
  )

  const addHeading = useCallback(() => {
    if (!currentTab) return
    const maxY = currentTab.tiles.reduce((m, t) => Math.max(m, t.y + t.h), 0)
    patchTab(currentTabId!, [
      ...currentTab.tiles,
      { i: `heading-${Date.now()}`, type: 'heading', text: 'Abschnitt', x: 0, y: maxY, w: 12, h: 1 },
    ])
  }, [config, currentTab, currentTabId])

  const addChart = useCallback(
    (chartKey: string) => {
      if (!currentTab) return
      const chartDef = getChartCardDef(chartKey)
      const maxY = currentTab.tiles.reduce((m, t) => Math.max(m, t.y + t.h), 0)
      patchTab(currentTabId!, [
        ...currentTab.tiles,
        {
          i: `${chartKey}-${Date.now()}`,
          type: 'chart',
          chartKey,
          x: 0,
          y: maxY,
          w: chartDef?.defaultW ?? 6,
          h: chartDef?.defaultH ?? 5,
        },
      ])
    },
    [config, currentTab, currentTabId],
  )

  const removeTile = useCallback(
    (tileId: string) => {
      if (!currentTab) return
      patchTab(currentTabId!, currentTab.tiles.filter((t) => t.i !== tileId))
    },
    [config, currentTab, currentTabId],
  )

  const updateLayout = useCallback(
    (tiles: DashboardTile[]) => { if (currentTabId) patchTab(currentTabId, tiles) },
    [config, currentTabId],
  )

  const changeVariant = useCallback(
    (tileId: string, variant: TileVariant) => {
      if (!currentTab) return
      patchTab(currentTabId!, currentTab.tiles.map((t) => (t.i === tileId ? { ...t, variant } : t)))
    },
    [config, currentTab, currentTabId],
  )

  const changeHeadingText = useCallback(
    (tileId: string, text: string) => {
      if (!currentTab) return
      patchTab(currentTabId!, currentTab.tiles.map((t) => (t.i === tileId ? { ...t, text } : t)))
    },
    [config, currentTab, currentTabId],
  )

  if (configQuery.isLoading) return <LoadingState label="Dashboard wird geladen…" />

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Mein Dashboard"
        description="Persönlich konfigurierbare KPI-Übersicht"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => qc.invalidateQueries({ queryKey: STATS_QUERY_KEY })}
            >
              <RefreshCw className={cn('w-4 h-4', stats.isFetching && 'animate-spin')} />
              Aktualisieren
            </Button>
            {isEditing && (
              <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
                <Plus className="w-4 h-4" />
                Kachel hinzufügen
              </Button>
            )}
            <Button
              variant={isEditing ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setIsEditing(!isEditing)}
            >
              {isEditing ? (
                <><Check className="w-4 h-4" />Fertig</>
              ) : (
                <><LayoutGrid className="w-4 h-4" />Dashboard anpassen</>
              )}
            </Button>
          </div>
        }
      />

      {stats.error && (
        <ErrorState
          title="Statistiken konnten nicht geladen werden"
          description={stats.error instanceof Error ? stats.error.message : 'Unbekannter Fehler'}
        />
      )}

      <TabBar
        tabs={config.tabs}
        activeId={currentTabId}
        isEditing={isEditing}
        onSelect={setActiveTabId}
        onRename={renameTab}
        onDelete={deleteTab}
        onAdd={addTab}
      />

      {currentTab && (
        <TabGrid
          key={currentTab.id}
          tiles={currentTab.tiles}
          isEditing={isEditing}
          statsData={stats.data}
          statsLoading={stats.isLoading}
          onLayoutChange={updateLayout}
          onRemove={removeTile}
          onVariantChange={changeVariant}
          onHeadingChange={changeHeadingText}
        />
      )}

      {isEditing && currentTab?.tiles.length === 0 && (
        <div className="flex justify-center pt-4">
          <Button variant="primary" onClick={() => setPickerOpen(true)}>
            <Plus className="w-4 h-4" />
            Erste Kachel hinzufügen
          </Button>
        </div>
      )}

      <TilePickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        existingKeys={existingCardKeys}
        onAddCard={addCard}
        onAddChart={addChart}
        onAddHeading={addHeading}
      />
    </div>
  )
}
